import { parseEventLogs } from "viem";
import { protocolErrorAbi } from "./errors.js";
import { projectCoreAbi } from "./project.js";
import { TAPEOUT_FEE } from "./tapeout.js";
import { nativeAmount } from "./types.js";
const uintOutput = [{ name: "", type: "uint256" }];
const tokenIdInput = (name) => [{ name, type: "uint256" }];
export const MAX_REFUND_BATCH = 20;
export const refundProjectCoreAbi = [
    ...projectCoreAbi,
    ...["nativeRefundCredit", "nativeTreasuryCredit"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [{ name: "beneficiary", type: "address" }],
        outputs: uintOutput,
    })),
    ...["withdrawNativeRefund", "withdrawNativeTreasuryCredit"].map((name) => ({
        type: "function",
        name,
        stateMutability: "nonpayable",
        inputs: [{ name: "receiver", type: "address" }],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "refundCredit",
        stateMutability: "view",
        inputs: [{ name: "beneficiary", type: "address" }],
        outputs: uintOutput,
    },
    ...["refundRegisteredCredits", "refundWithdrawn", "refundableEscrow"].map((name) => ({
        type: "function",
        name,
        stateMutability: "view",
        inputs: [],
        outputs: uintOutput,
    })),
    {
        type: "function",
        name: "claimNandRefund",
        stateMutability: "nonpayable",
        inputs: tokenIdInput("nandId"),
        outputs: [{ name: "credit", type: "uint256" }],
    },
    {
        type: "function",
        name: "claimNandRefundMany",
        stateMutability: "nonpayable",
        inputs: [{ name: "nandIds", type: "uint256[]" }],
        outputs: [{ name: "credit", type: "uint256" }],
    },
    {
        type: "function",
        name: "claimMinerRefund",
        stateMutability: "nonpayable",
        inputs: tokenIdInput("minerId"),
        outputs: [{ name: "credit", type: "uint256" }],
    },
    {
        type: "function",
        name: "claimMinerRefundMany",
        stateMutability: "nonpayable",
        inputs: [{ name: "minerIds", type: "uint256[]" }],
        outputs: [{ name: "credit", type: "uint256" }],
    },
    {
        type: "function",
        name: "withdrawRefund",
        stateMutability: "nonpayable",
        inputs: [{ name: "receiver", type: "address" }],
        outputs: [
            { name: "paymentAssetAmount", type: "uint256" },
            { name: "nativeSupplementAmount", type: "uint256" },
        ],
    },
];
export const refundableAssetNftAbi = [
    ...protocolErrorAbi,
    {
        type: "function",
        name: "ownerOf",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "owner", type: "address" }],
    },
    {
        type: "function",
        name: "refunded",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "", type: "bool" }],
    },
    {
        type: "function",
        name: "ownershipEpoch",
        stateMutability: "view",
        inputs: [{ name: "tokenId", type: "uint256" }],
        outputs: [{ name: "epoch", type: "uint64" }],
    },
];
export const refundEventAbi = [
    {
        type: "event",
        name: "RefundAssetCredited",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "beneficiary", type: "address", indexed: true },
            { name: "paymentAsset", type: "address", indexed: true },
            { name: "assetContract", type: "address", indexed: false },
            { name: "tokenId", type: "uint256", indexed: false },
            { name: "assetKind", type: "uint8", indexed: false },
            { name: "amount", type: "uint256", indexed: false },
            { name: "beneficiaryCreditAfter", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RefundAssetWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "beneficiary", type: "address", indexed: true },
            { name: "paymentAsset", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: false },
            { name: "amount", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RefundCredited",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "beneficiary", type: "address", indexed: true },
            { name: "assetContract", type: "address", indexed: true },
            { name: "tokenId", type: "uint256", indexed: false },
            { name: "assetKind", type: "uint8", indexed: false },
            { name: "amountWei", type: "uint256", indexed: false },
            { name: "beneficiaryCreditAfter", type: "uint256", indexed: false },
        ],
    },
    {
        type: "event",
        name: "RefundWithdrawn",
        inputs: [
            { name: "project", type: "address", indexed: true },
            { name: "beneficiary", type: "address", indexed: true },
            { name: "receiver", type: "address", indexed: true },
            { name: "amountWei", type: "uint256", indexed: false },
        ],
    },
];
export function previewNandRefund(nandPrice) {
    return nativeAmount(nandPrice);
}
export function previewMinerRefund(nandPrice) {
    return nativeAmount(nandPrice * 5n + TAPEOUT_FEE);
}
export function assertRefundBatch(ids) {
    if (ids.length < 1 || ids.length > MAX_REFUND_BATCH) {
        throw new RangeError(`Refund batch must contain 1 to ${MAX_REFUND_BATCH} asset IDs`);
    }
}
export function parseRefundReceipt(receipt) {
    return parseEventLogs({ abi: refundEventAbi, logs: receipt.logs, strict: false });
}
export function refundSolvency(snapshot) {
    return {
        conserved: snapshot.refundableEscrow ===
            snapshot.unregisteredLiability + snapshot.registeredCredits + snapshot.withdrawnRefunds,
        solvent: snapshot.availableBalance >= snapshot.unregisteredLiability + snapshot.registeredCredits,
    };
}
//# sourceMappingURL=refunds.js.map